import httpClient from "@utils/httpClient";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface Group {
  id: number;
  name: string;
  description: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  member_count: number;
  assessment_count: number;
}

export interface GroupMember {
  userId: number;
  name: string;
  email: string;
  source: string;
  created_at: string;
}

export interface GroupAssessment {
  assignment_id: number;
  assessment_id: number;
  assessmentTitle: string;
  status: string;
  startTime: string | null;
  endTime: string | null;
  assigned_at: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data: T | null;
  error: { message: string } | null;
}

// ============================================================================
// GROUP CRUD METHODS
// ============================================================================

export const createGroup = async (
  name: string,
  description?: string
): Promise<ApiResponse<Group>> => {
  try {
    const response = await httpClient.post("/groups", { name, description });
    return { success: true, data: response.data || response, error: null };
  } catch (error: any) {
    console.error("createGroup error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to create group" },
    };
  }
};

export const getAllGroups = async (): Promise<ApiResponse<Group[]>> => {
  try {
    const response = await httpClient.get("/groups");
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    console.error("getAllGroups error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to fetch groups" },
    };
  }
};

export const getGroupById = async (
  groupId: number
): Promise<ApiResponse<Group>> => {
  try {
    const response = await httpClient.get(`/groups/${groupId}`);
    return { success: true, data: response.data || response, error: null };
  } catch (error: any) {
    console.error("getGroupById error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to fetch group" },
    };
  }
};

export const updateGroup = async (
  groupId: number,
  name?: string,
  description?: string
): Promise<ApiResponse<Group>> => {
  try {
    const response = await httpClient.put(`/groups/${groupId}`, {
      name,
      description,
    });
    return { success: true, data: response.data || response, error: null };
  } catch (error: any) {
    console.error("updateGroup error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to update group" },
    };
  }
};

export const deleteGroup = async (
  groupId: number
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.delete(`/groups/${groupId}`);
    return { success: true, data: response, error: null };
  } catch (error: any) {
    console.error("deleteGroup error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to delete group" },
    };
  }
};

// ============================================================================
// GROUP MEMBERS METHODS
// ============================================================================

export const getGroupMembers = async (
  groupId: number
): Promise<ApiResponse<GroupMember[]>> => {
  try {
    const response = await httpClient.get(`/groups/${groupId}/members`);
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    console.error("getGroupMembers error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to fetch group members" },
    };
  }
};

// ============================================================================
// GROUP-ASSESSMENT ASSIGNMENT METHODS
// ============================================================================

export const assignAssessmentToGroup = async (
  groupId: number,
  assessmentId: number
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post(`/groups/${groupId}/assessments`, {
      assessment_id: assessmentId,
    });
    return { success: true, data: response.data || response, error: null };
  } catch (error: any) {
    console.error("assignAssessmentToGroup error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to assign assessment" },
    };
  }
};

export const getGroupAssessments = async (
  groupId: number
): Promise<ApiResponse<GroupAssessment[]>> => {
  try {
    const response = await httpClient.get(`/groups/${groupId}/assessments`);
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    console.error("getGroupAssessments error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to fetch group assessments" },
    };
  }
};

export const removeAssessmentFromGroup = async (
  groupId: number,
  assessmentId: number
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.delete(
      `/groups/${groupId}/assessments/${assessmentId}`
    );
    return { success: true, data: response, error: null };
  } catch (error: any) {
    console.error("removeAssessmentFromGroup error:", error);
    return {
      success: false,
      data: null,
      error: { message: error.message || "Failed to remove assessment" },
    };
  }
};
